<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMenuStore, type MenuNode } from '@/stores/menu'
import { useRoute, useRouter } from 'vue-router'
import { useBreadcrumbStore, type BreadcrumbItem } from '@/stores/breadcrumb'
import { useFavoritesStore } from '@/stores/favorites'
import { useAuthStore } from '@/stores/auth'
import { usePersonalizzazione } from '@/composables/usePersonalizzazione'
import AzioniDinamiche from '@/components/common/AzioniDinamiche.vue'
import { usePluginContextStore } from '@/stores/pluginContext'
import { useLegacyLink } from '@/composables/useLegacyLink'
import { ROUTE_HUB_ADMIN, ROUTE_HUB_CONFIGURAZIONE, titoloVoceHub, useHubVoci } from '@/composables/useHubVoci'

const { t, te } = useI18n()

const SEGMENT_LABELS: Record<string, string> = {
  admin: 'Amministrazione',
  roles: 'Ruoli',
  'user-permissions': 'Permessi Utente',
  diagnostics: 'Diagnostica',
  guide: 'Guide',
  calendario: 'Calendario',
  appuntamenti: 'Appuntamenti',
  tipologie: 'Tipologie',
  ticket: 'Ticket',
  helpdesk: 'Helpdesk',
  config: 'Configurazione',
  dashboard: 'Dashboard',
  nuovo: 'Nuovo',
}

/** Chiave camelCase del segmento/etichetta («Magazzino» → magazzino, «user-permissions» → userPermissions). */
function segKey(s: string): string {
  const parts = s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
  return parts.map((p, i) => (i === 0 ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())).join('')
}

const menuStore = useMenuStore()

/** Voce di menu con questa etichetta italiana (le radici come «Protocollo» non hanno una route). */
function findMenuNodeByLabel(nodes: MenuNode[], label: string): MenuNode | undefined {
  const l = label.trim().toLowerCase()
  for (const n of nodes) {
    if (n.label?.trim().toLowerCase() === l) return n
    const f = n.children ? findMenuNodeByLabel(n.children, label) : undefined
    if (f) return f
  }
  return undefined
}

function findMenuNode(nodes: MenuNode[], path: string): MenuNode | undefined {
  for (const n of nodes) {
    if (n.springRoute === path) return n
    const f = n.children ? findMenuNode(n.children, path) : undefined
    if (f) return f
  }
  return undefined
}

/** Etichetta tradotta: voce di menu (per route) → chiave layout.breadcrumb.<etichetta> → testo di oggi. */
function tradotta(item: BreadcrumbItem, isLast: boolean): string {
  const path = item.to ?? item.path ?? (isLast ? route.path : undefined)
  const node = (path ? findMenuNode(menuStore.items, path) : undefined) ?? findMenuNodeByLabel(menuStore.items, item.label)
  if (node && te(`menu.${node.id}`)) return t(`menu.${node.id}`)
  // Titolo della pagina (meta.title): si traduce con la chiave route.<nome della route>.
  const rk = `route.${String(route.name ?? '')}`
  if (isLast && item.label === route.meta.title && te(rk)) return t(rk)
  const k = `layout.breadcrumb.${segKey(item.label)}`
  return te(k) ? t(k) : item.label
}

const route = useRoute()
const router = useRouter()
const breadcrumbStore = useBreadcrumbStore()
const favoritesStore = useFavoritesStore()
const auth = useAuthStore()

/**
 * Breadcrumb ricavato dall'indirizzo: un segmento per parte del percorso («effetti-da-consegnare» → «Effetti da
 * consegnare»). L'ultimo segmento usa il titolo della pagina (meta.title) quando la route lo dichiara.
 */
function autoGenerate(fullPath: string): BreadcrumbItem[] {
  const parti = fullPath.split('?')[0]!.split('/')
  const items: BreadcrumbItem[] = []
  parti.forEach((s, i) => {
    if (s === '' || !isNaN(Number(s))) return
    const testo = s.replace(/-/g, ' ')
    items.push({
      label: SEGMENT_LABELS[s] ?? (testo.charAt(0).toUpperCase() + testo.slice(1)),
      path: parti.slice(0, i + 1).join('/'),
    })
  })
  const titolo = route.meta.title as string | undefined
  const ultimo = items.at(-1)
  if (titolo && ultimo) ultimo.label = titolo
  return items
}

// Le pagine di configurazione non sono più nell'albero del menu (sotto «Configurazione» c'è solo l'hub):
// il percorso è «Configurazione › titolo della pagina».
const { voceDiRoute, hubDiRoute } = useHubVoci()

const breadcrumbRoutes = computed<BreadcrumbItem[]>(() => {
  const voceHub = voceDiRoute(route.path)
  if (voceHub) {
    return [
      hubDiRoute(route.path) === 'admin'
        ? { label: 'Configurazione Admin', to: ROUTE_HUB_ADMIN, path: ROUTE_HUB_ADMIN }
        : { label: 'Configurazione', to: ROUTE_HUB_CONFIGURAZIONE, path: ROUTE_HUB_CONFIGURAZIONE },
      { label: titoloVoceHub(voceHub, t, te) },
    ]
  }
  if (breadcrumbStore.items !== null) return breadcrumbStore.items
  const meta = route.meta.breadcrumb as BreadcrumbItem[] | undefined
  if (meta?.length) return meta
  return autoGenerate(route.fullPath)
})

const currentPageLabel = computed(() => {
  const crumbs = breadcrumbRoutes.value
  const last = crumbs.at(-1)
  return last ? tradotta(last, true) : route.path
})

const isCurrentFavorite = computed(() => favoritesStore.isFavorite(route.path))

function navigate() {
  router.push('/')
}

function toggleFavorite() {
  favoritesStore.toggle({ path: route.path, label: currentPageLabel.value })
}

async function handleLogout() {
  await auth.logout()
  // La pagina di login della SPA è `/login`: `/auth/login` è l'ENDPOINT del backend, e un
  // router.push su quel path non trova alcuna rotta — il browser lo chiedeva al server e
  // rispondeva 404. Uscire dal portale finiva quindi su una pagina di errore.
  router.push('/login')
}

const idPagina = computed(() => route.meta.idPagina ?? '')
const { config: layoutConfig } = usePersonalizzazione(idPagina)
const pluginCtx = usePluginContextStore()

// ── Bottone "Apri scheda classica" ───────────────────────────────────────────
const { abilitato: legacyAbilitato, legacyUrlFor } = useLegacyLink()

const legacyUrl = computed<string | null>(() => legacyUrlFor(route.path))

function apriSchedaClassica() {
  const url = legacyUrl.value
  // Naviga nella STESSA scheda (come le voci di menu legacy), non in un nuovo tab.
  if (url) window.location.href = url
}
</script>

<template>
  <div class="layout-breadcrumb-container">
    <nav class="layout-breadcrumb">
      <ol>
        <li>
          <i
            class="pi pi-home"
            style="cursor:pointer"
            @click="navigate"
          />
        </li>
        <li><i class="pi pi-angle-right" /></li>
        <template
          v-for="(item, index) in breadcrumbRoutes"
          :key="index"
        >
          <li>
            <span
              v-if="item.to"
              class="cursor-pointer hover:text-primary"
              @click="router.push(item.to)"
            >{{ tradotta(item, index === breadcrumbRoutes.length - 1) }}</span>
            <span v-else>{{ tradotta(item, index === breadcrumbRoutes.length - 1) }}</span>
          </li>
          <li v-if="index !== breadcrumbRoutes.length - 1">
            <i class="pi pi-angle-right" />
          </li>
        </template>
      </ol>
    </nav>

    <!-- Apri scheda classica — centrato nel breadcrumb, visibile solo se abilitato
         e la route ha un mapping .xhtml in funzioni_2 -->
    <div
      v-if="legacyAbilitato && legacyUrl !== null"
      class="legacy-link-center"
    >
      <Button
        :label="t('layout.breadcrumb.apriSchedaClassica', 'Apri scheda classica')"
        icon="pi pi-external-link"
        size="small"
        outlined
        @click="apriSchedaClassica"
      />
    </div>

    <AzioniDinamiche
      :config="layoutConfig"
      posizione="TOP_BAR"
      :entita-id="pluginCtx.entitaId"
      :entita-tipo="pluginCtx.entitaTipo"
      :contesto="pluginCtx.contesto"
    />

    <div class="layout-breadcrumb-buttons">
      <Button
        v-tooltip.bottom="isCurrentFavorite ? t('layout.breadcrumb.rimuoviPreferiti', 'Rimuovi dai preferiti') : t('layout.breadcrumb.aggiungiPreferiti', 'Aggiungi ai preferiti')"
        :icon="isCurrentFavorite ? 'pi pi-bookmark-fill' : 'pi pi-bookmark'"
        :class="{ 'text-primary': isCurrentFavorite }"
        rounded
        text
        plain
        @click="toggleFavorite"
      />
      <Button
        v-tooltip.bottom="t('layout.breadcrumb.esci', 'Esci')"
        icon="pi pi-power-off"
        rounded
        text
        plain
        @click="handleLogout"
      />
    </div>
  </div>
</template>

<style scoped>
/* Bottone "Apri scheda classica" centrato nel flusso del breadcrumb:
   occupa lo spazio flessibile tra i crumb (sinistra) e azioni/pulsanti (destra)
   e centra il proprio contenuto, senza sovrapporsi a eventuali altri bottoni. */
.legacy-link-center {
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  min-width: 0;
}
</style>
